/**
 * Cached Shopify section HTML fetches for PDP.
 * TTL: 30 minutes (memory + localStorage). Dedupes in-flight requests.
 * 429/errors are never cached; inflight is cleared so callers can refetch.
 * Safe to load multiple times; only installs once.
 */
(function (global) {
  if (global.fetchSectionHtml && global.fetchSectionHtml._sepPdpCached) return

  var TTL_MS = 30 * 60 * 1000
  var PREFIX = 'sepPdpSec:v3:'
  var MAX_429_RETRIES = 3
  var memory = new Map()
  var inflight = new Map()

  function normalizeUrl(url) {
    try {
      var u = new URL(url, location.origin)
      var params = new URLSearchParams(u.search)
      var keys = []
      params.forEach(function (_v, k) {
        keys.push(k)
      })
      keys.sort()
      var sorted = new URLSearchParams()
      keys.forEach(function (k) {
        params.getAll(k).forEach(function (v) {
          sorted.append(k, v)
        })
      })
      var qs = sorted.toString()
      return u.pathname + (qs ? '?' + qs : '')
    } catch (e) {
      return String(url)
    }
  }

  function storageKey(key) {
    return PREFIX + key
  }

  function read(key) {
    var now = Date.now()
    var mem = memory.get(key)
    if (mem) {
      if (now - mem.ts < TTL_MS && typeof mem.text === 'string' && mem.text.length) {
        return mem.text
      }
      memory.delete(key)
    }
    try {
      var raw = localStorage.getItem(storageKey(key))
      if (!raw) return null
      var parsed = JSON.parse(raw)
      if (
        !parsed ||
        typeof parsed.text !== 'string' ||
        !parsed.text.length ||
        now - parsed.ts >= TTL_MS
      ) {
        localStorage.removeItem(storageKey(key))
        return null
      }
      memory.set(key, parsed)
      return parsed.text
    } catch (e) {
      return null
    }
  }

  function pruneStorage() {
    var entries = []
    for (var i = 0; i < localStorage.length; i++) {
      var k = localStorage.key(i)
      if (!k || k.indexOf(PREFIX) !== 0) continue
      var ts = 0
      try {
        ts = JSON.parse(localStorage.getItem(k) || '{}').ts || 0
      } catch (e) {}
      entries.push({ k: k, ts: ts })
    }
    entries.sort(function (a, b) {
      return a.ts - b.ts
    })
    var removeCount = Math.max(1, Math.ceil(entries.length / 2))
    for (var j = 0; j < removeCount; j++) {
      try {
        localStorage.removeItem(entries[j].k)
      } catch (e) {}
    }
  }

  function write(key, text) {
    if (typeof text !== 'string' || !text.length) return
    var entry = { ts: Date.now(), text: text }
    memory.set(key, entry)
    try {
      localStorage.setItem(storageKey(key), JSON.stringify(entry))
    } catch (e) {
      try {
        pruneStorage()
        localStorage.setItem(storageKey(key), JSON.stringify(entry))
      } catch (e2) {}
    }
  }

  function clearKey(key) {
    memory.delete(key)
    try {
      localStorage.removeItem(storageKey(key))
    } catch (e) {}
  }

  function fetchSectionHtml(url, init, retryCount) {
    retryCount = retryCount || 0
    var key = normalizeUrl(url)
    var cached = read(key)
    if (cached != null) return Promise.resolve(cached)

    if (inflight.has(key)) return inflight.get(key)

    var promise = fetch(url, init)
      .then(function (res) {
        if (!res.ok) {
          var err = new Error('section fetch failed ' + res.status)
          err.status = res.status
          throw err
        }
        return res.text()
      })
      .then(function (text) {
        // Never cache empty/error bodies
        if (typeof text === 'string' && text.length) write(key, text)
        else clearKey(key)
        inflight.delete(key)
        return text
      })
      .catch(function (err) {
        // Never store failed responses (or the key)
        clearKey(key)
        inflight.delete(key)

        if (err && err.status === 429 && retryCount < MAX_429_RETRIES) {
          var delay = 1500 * Math.pow(2, retryCount)
          var retryPromise = new Promise(function (resolve, reject) {
            setTimeout(function () {
              // Drop this placeholder so the retry can claim inflight cleanly
              if (inflight.get(key) === retryPromise) inflight.delete(key)
              fetchSectionHtml(url, init, retryCount + 1).then(resolve, reject)
            }, delay)
          })
          // Coalesce clicks during backoff onto the same delayed retry
          inflight.set(key, retryPromise)
          return retryPromise
        }
        throw err
      })

    inflight.set(key, promise)
    return promise
  }

  fetchSectionHtml.clear = function (url) {
    if (!url) return
    var key = normalizeUrl(url)
    clearKey(key)
    inflight.delete(key)
  }

  fetchSectionHtml._sepPdpCached = true
  global.fetchSectionHtml = fetchSectionHtml
})(typeof window !== 'undefined' ? window : this)
